import React, { useMemo } from "react";
import { AddOnLimits, AddOnLimitsProvider } from "./AddOnLimitsContext";
import { CreatePurchaseAddOnList, CreatePurchaseAddOnListProps } from "./CreatePurchaseAddOnList";
import { CreatePurchaseAddOnSkeleton } from "./CreatePurchaseAddOnSkeleton";
import { useAddOnLineItemsForItem } from "./hooks/useAddOnLineItemsForItem";
import { AddonLineItem, useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface CreatePurchaseAddOnFieldProps
    extends Omit<CreatePurchaseAddOnListProps, "lineItems" | "onLineItemUpdate" | "onChoiceLineItemUpdate"> {
    lineItems?: AddonLineItem[];
    /*  (Optional) active item index for package products */
    activeItemIndex?: number;
}

export const CreatePurchaseAddOnField = React.memo(
    ({ lineItems, activeItemIndex: packageActiveItemIndex, ...rest }: CreatePurchaseAddOnFieldProps) => {
        // A package field edits its own item, not the store's active one, so every value here must come from that item.
        const storeActiveItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
        const itemIndex = packageActiveItemIndex ?? storeActiveItemIndex;
        const isInitialized = useCreatePurchaseStore((state) => state.purchaseId);
        const isPastDated = useCreatePurchaseStore((state) => state.isPastDatedItem(itemIndex));
        const guestCount = useCreatePurchaseStore((state) => state.purchase.items[itemIndex]?.quantity ?? 0);
        const itemRestrictionErrors = useCreatePurchaseStore((state) => state.getAddOnRestrictionErrors(itemIndex));
        const { overrideRestrictions, handleOverrideChange, handleQuantityChange, handleChoiceChange } =
            useAddOnLineItemsForItem(itemIndex, lineItems);

        const addOnLimits = useMemo<AddOnLimits>(() => {
            return {
                isPastDated,
                guestCount,
                errors: itemRestrictionErrors?.errors,
                catalogItemOpen: itemRestrictionErrors?.catalogItemOpen,
                overrideRestrictions,
                onOverrideRestrictionsChange: handleOverrideChange,
            };
        }, [isPastDated, guestCount, itemRestrictionErrors, overrideRestrictions, handleOverrideChange]);

        // Till we have an item, don't render the rest of the catalog to avoid extra work on load of the page
        // This acts as a performance optimization to reduce the load the browser does when user selects and experience
        if (!isInitialized) {
            return <CreatePurchaseAddOnSkeleton />;
        }

        return (
            <AddOnLimitsProvider value={addOnLimits}>
                <CreatePurchaseAddOnList
                    lineItems={lineItems}
                    onLineItemUpdate={handleQuantityChange}
                    onChoiceLineItemUpdate={handleChoiceChange}
                    {...rest}
                />
            </AddOnLimitsProvider>
        );
    },
);

CreatePurchaseAddOnField.displayName = "CreatePurchaseAddOnField";
